import { omitBy, isUndefined } from "lodash";
import queryString from "query-string";
import {
  IPlaque,
  IVictimFull,
  Locale,
  UniqueSlugPayload,
  UniqueSlugResult,
} from "../types/common";

import { adminClient } from "../client/adminClient";
import { IArticle } from "../types/article";
import { Icon } from "../types/Icon";
import {
  ChangeVictimStatusPayload,
  GetAdminVictimsOutputPayload,
  UpdateVictimPayload,
} from "../types/api/victim";
import {
  CreateArticlePayload,
  UpdateArticlePayload,
} from "../types/api/article";
import {
  ChangePlaqueStatusPayload,
  UpdatePlaquePayload,
} from "../types/api/plaque";
import { Brigade, BrigadeForm, BrigadeFull, UpdateBrigadeInput } from "../types/brigade";
import { toUtcNoon } from '../helpers/toUtcNoonISOString';

async function getVictims(): Promise<GetAdminVictimsOutputPayload> {
  return adminClient.get("/victims");
}

async function getIcons(): Promise<Icon[]> {
  return adminClient.get("/icons");
}

async function changeVictimStatus({
  id,
  status,
  slug,
}: ChangeVictimStatusPayload): Promise<void> {
  return adminClient.patch(`/victims/${id}/status`, {
    status,
    slug: status === "approved" ? slug : undefined,
  });
}

async function updateVictim(
  id: number,
  payload: UpdateVictimPayload,
  locale: Locale,
): Promise<IVictimFull> {
  const {
    noDeathDate,
    brigade,
    fatherName,
    birthDate,
    deathDate,
    ...victimProps
  } = payload;

  return adminClient.patch(`/victims/${id}?locale=${locale}`, {
    ...victimProps,
    fatherName: fatherName === "" ? null : fatherName,
    brigadeId: brigade === null ? null : brigade?.value,
    birthDate: birthDate ? toUtcNoon(birthDate) : birthDate,
    deathDate: deathDate ? toUtcNoon(deathDate) : deathDate,
  });
}

async function createArticle(payload: CreateArticlePayload): Promise<IArticle> {
  const { icon, date, ...articleProps } = payload;

  return adminClient.post("/articles", {
    ...articleProps,
    iconId: icon?.value,
    date: date ? toUtcNoon(date) : undefined,
  });
}

async function updateArticle(
  id: number,
  payload: UpdateArticlePayload,
): Promise<IArticle> {
  const { icon, date, ...articleProps } = payload;

  return adminClient.patch(`/articles/${id}`, {
    ...articleProps,
    iconId: icon?.value,
    date: date ? toUtcNoon(date) : undefined,
  });
}

async function deleteArticle(id: number) {
  return adminClient.delete(`/articles/${id}`);
}

async function getPlaques(): Promise<IPlaque[]> {
  return adminClient.get("/plaques");
}

async function changePlaqueStatus({
  id,
  status,
}: ChangePlaqueStatusPayload): Promise<void> {
  return adminClient.patch(`/plaques/${id}/status`, { status });
}

async function updatePlaque(
  id: number,
  payload: UpdatePlaquePayload,
  locale: Locale,
): Promise<IPlaque> {
  const { victim, ...data } = payload;

  return adminClient.patch(`/plaques/${id}?locale=${locale}`, {
    ...omitBy(data, isUndefined),
    victimId: victim?.value,
  });
}

export function getPreferredSlug({
  victimId,
  slug,
  locale,
}: UniqueSlugPayload): Promise<UniqueSlugResult> {
  const queryParams = queryString.stringify(
    { victimId: slug ? undefined : victimId, slug, locale },
    { skipEmptyString: true },
  );

  return adminClient.get(`/victims/preferred-slug?${queryParams}`);
}

export function createBrigade(data: BrigadeForm): Promise<Brigade> {
  return adminClient.post("/brigades", data);
}

export function updateBrigade({ id, ...data}: UpdateBrigadeInput): Promise<Brigade> {
  return adminClient.patch(`/brigades/${id}`, data);
}

export function removeBrigade(id: number): Promise<Brigade> {
  return adminClient.delete(`/brigades/${id}`);
}

export const adminService = {
  getVictims,
  getIcons,
  changeVictimStatus,
  updateVictim,
  createArticle,
  updateArticle,
  deleteArticle,
  getPlaques,
  changePlaqueStatus,
  updatePlaque,
  getPreferredSlug,
  createBrigade,
  updateBrigade,
  removeBrigade,
};
